import { ExternalLink,
  LayoutDashboard,
  UtensilsCrossed,
  Compass,
  CalendarDays,
  MapPin,
  Layers,
  Building2,
  Users,
  Shield,
  MessageSquare,
  Send,
  Inbox,
  Star,
  CreditCard,
  MousePointerClick,
  Ticket,
  Radio,
  ScanLine,
  ScrollText,
  ListChecks,
  Landmark,
  Images,
  Settings2,
  Globe,
  Wallet,
  DatabaseBackup,
  Briefcase,
  Languages,
  ClipboardList,
  MapPinned,
  LayoutGrid,
} from "lucide-react";

export type PageKey =
  | "dashboard"
  | "users"
  | "membership"
  | "payments"
  | "listing-fees"
  | "admins"
  | "support"
  | "reviews"
  | "cities"
  | "carousels"
  | "settings"
  | "eat"
  | "do"
  | "happening"
  | "all"
  | "tickets"
  | "live-events"
  | "lippu"
  | "contracts"
  | "landmarks"
  | "backups"
  | "partners"
  | "publish-queue"
  | "crm-users"
  | "business"
  | "telegram";

export const PAGE_KEYS = new Set<PageKey>([
  "dashboard",
  "users",
  "membership",
  "payments",
  "listing-fees",
  "admins",
  "support",
  "reviews",
  "cities",
  "carousels",
  "settings",
  "eat",
  "do",
  "happening",
  "all",
  "tickets",
  "live-events",
  "lippu",
  "contracts",
  "landmarks",
  "backups",
  "partners",
  "publish-queue",
  "crm-users",
  "business",
  "telegram",
]);

export const PAGES: {
  key: PageKey;
  label: string;
  navLabel: string;
  icon: any;
  section?: "eat" | "do" | "happening";
  description: string;
  publicPath?: string;
}[] = [
  {
    key: "telegram",
    label: "Telegram Bot",
    navLabel: "Telegram",
    icon: Send,
    description: "Admin Telegram bot — live alerts for users, contacts, onboarding, reviews and CRM.",
  },
  {
    key: "dashboard",
    label: "Dashboard",
    navLabel: "Dashboard",
    icon: LayoutDashboard,
    description: "Overview of all your content at a glance.",
  },
  {
    key: "users",
    label: "Users",
    navLabel: "Users",
    icon: Users,
    description: "All registered users — admins, providers and sign-in activity.",
  },
  {
    key: "membership",
    label: "Membership",
    navLabel: "Members",
    icon: CreditCard,
    description: "Full member list. Manage Gold & Black cards and manually upgrade users.",
    publicPath: "/profile?tab=membership",
  },
  {
    key: "payments",
    label: "Payments",
    navLabel: "Payments",
    icon: Wallet,
    description: "Live ticket orders, Stripe status, and revenue from the database.",
  },
  {
    key: "listing-fees",
    label: "Listing Fees",
    navLabel: "Fees",
    icon: CreditCard,
    description:
      "Charge a listing fee when you add a place, ticket, event or landmark — card, Apple/Google Pay, bank transfer, or free.",
  },
  {
    key: "cities",
    label: "Cities",
    navLabel: "Cities",
    icon: Globe,
    description: "Add, edit and remove cities used across the site.",
  },
  {
    key: "carousels",
    label: "Home carousel",
    navLabel: "Carousel",
    icon: Images,
    description: "Pick which places appear in the homepage carousels per city.",
    publicPath: "/",
  },
  {
    key: "settings",
    label: "Site settings",
    navLabel: "Settings",
    icon: Settings2,
    description: "Hero images, AI prompts, hero badge and the Aurora unlock section.",
    publicPath: "/",
  },
  {
    key: "eat",
    label: "Eat",
    navLabel: "Eat",
    icon: UtensilsCrossed,
    section: "eat",
    description: "Restaurants, cafés, bars and bakeries shown on /eat.",
    publicPath: "/eat",
  },
  {
    key: "do",
    label: "Do",
    navLabel: "Do",
    icon: Compass,
    section: "do",
    description: "Museums, saunas, parks and experiences shown on /do.",
    publicPath: "/do",
  },
  {
    key: "happening",
    label: "Events",
    navLabel: "Events",
    icon: CalendarDays,
    section: "happening",
    description: "Upcoming events, festivals and gigs shown on /happening.",
    publicPath: "/happening",
  },
  {
    key: "all",
    label: "All places",
    navLabel: "Places",
    icon: Layers,
    description: "Every place across every section, unfiltered.",
  },
  {
    key: "live-events",
    label: "Live events",
    navLabel: "Live",
    icon: Radio,
    description: "Imported event sources, sync logs and curated event listings.",
    publicPath: "/happening",
  },
  {
    key: "lippu",
    label: "Event scraper",
    navLabel: "Scraper",
    icon: ScanLine,
    description:
      "Paste any event URL (Lippu, Tiketti, Ticketmaster, venue page) and we'll extract image, info, date, venue and price. Auto-resyncs every 30 min and removes ended events.",
  },
  {
    key: "admins",
    label: "Admin & Audit",
    navLabel: "Admins",
    icon: Shield,
    description: "Manage admins and view every action, login/logout, devices, IPs and time on platform.",
  },
  {
    key: "support",
    label: "Support inbox",
    navLabel: "Support",
    icon: MessageSquare,
    description: "Messages submitted from the contact form. Reply to users by email.",
    publicPath: "/contact",
  },
  {
    key: "reviews",
    label: "Reviews",
    navLabel: "Reviews",
    icon: Star,
    description: "All reviews users have posted. Remove any review you want to take down.",
  },
  {
    key: "tickets",
    label: "Tickets",
    navLabel: "Tickets",
    icon: Ticket,
    description: "Manage ticket products: prices, affiliate links and external payment links.",
    publicPath: "/tickets",
  },
  {
    key: "landmarks",
    label: "Landmarks",
    navLabel: "Landmarks",
    icon: Landmark,
    description: 'Manage the "Landmarks Worth A Detour" cards shown on the Things to do page.',
    publicPath: "/do",
  },
  {
    key: "partners",
    label: "Partners",
    navLabel: "Partners",
    icon: MousePointerClick,
    description: "Live partner cards: impressions, listing views, and outbound clicks from the last 30 days.",
  },
  {
    key: "publish-queue",
    label: "Publish Queue",
    navLabel: "Queue",
    icon: ListChecks,
    description: "Review approved partner drafts and publish them live to Eat, Do, or Happening.",
  },
  {
    key: "business",
    label: "Business",
    navLabel: "Business",
    icon: Briefcase,
    description: "Business listings, services, corporate events, categories, homepage and analytics for the Business side of What.",
    publicPath: "/business",
  },
  {
    key: "contracts",
    label: "Client Contracts",
    navLabel: "Contracts",
    icon: ScrollText,
    description: "Manage client contracts, send PDFs, track signatures and auto-reminders.",
  },
  {
    key: "backups",
    label: "Database",
    navLabel: "Backups",
    icon: DatabaseBackup,
    description: "Create, download, restore and prune database backups.",
  },
];

export type AdminNavLink = {
  id: string;
  label: string;
  to: string;
  icon: any;
  badge?: "apps" | "tf-pending";
};

export const ADMIN_NAV: {
  title: string;
  pages?: PageKey[];
  links?: AdminNavLink[];
  linksFirst?: boolean;
}[] = [
  { title: "Home", pages: ["dashboard", "business"] },
  {
    title: "Partners",
    linksFirst: true,
    links: [
      { id: "apps", label: "Apps", to: "/crm/applications", icon: ClipboardList, badge: "apps" },
      { id: "locations", label: "Locations", to: "/admin/locations", icon: MapPinned },
      {
        id: "affiliate-links",
        label: "Affiliate links",
        to: "/admin/affiliate-links",
        icon: ExternalLink,
      },
    ],
    pages: ["partners", "contracts", "publish-queue"],
  },
  {
    title: "Content",
    pages: ["eat", "do", "happening", "all", "landmarks", "carousels", "settings", "cities"],
    links: [
      { id: "city-tiles", label: "City Tiles", to: "/admin/city-tiles", icon: LayoutGrid },
      { id: "districts", label: "Districts", to: "/admin/districts", icon: MapPinned, badge: "tf-pending" },
      { id: "tf-users", label: "Train Factory users", to: "/admin/train-factory-users", icon: Users },

      { id: "fi", label: "Finnish", to: "/admin/translations", icon: Languages },
    ],
  },
  { title: "Sell", pages: ["live-events", "lippu", "tickets", "payments", "listing-fees"] },
  { title: "People", pages: ["users", "membership", "admins"] },
  { title: "Inbox", pages: ["reviews", "support"] },
  { title: "System", pages: ["backups", "telegram"] },
];
